import React, { useState } from "react";
import dateFormat from "dateformat";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import DownloadIcon from "@mui/icons-material/Download";
import InvoiceTable from "./InvoiceTable";
import { axiosConfig } from "../../axios/axiosConfig";

const InvoiceDashboard = () => {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(false);

  const handleDownload = async () => {
    if (!fromDate || !toDate) {
      alert("Please select both From and To dates.");
      return;
    }
    const formattedFrom = dateFormat(new Date(fromDate), "yyyy-mm-dd");
    const formattedTo = dateFormat(new Date(toDate), "yyyy-mm-dd");
    setLoading(true);
    try {
      const response = await axiosConfig({
        method: "post",
        url: "/tnibroadmin/invoices",
        data: { from_date: formattedFrom, to_date: formattedTo, deleted: "false" },
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `invoice_${formattedFrom}_to_${formattedTo}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Download failed:", err);
      alert("Invoice download failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>
      {/* Header */}
      <Box
        sx={{
          background: "#04343a",
          borderRadius: "12px",
          px: 3,
          py: 2,
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          boxShadow: "0 4px 20px rgba(4,52,58,0.3)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <ReceiptLongIcon sx={{ color: "#fff", fontSize: 32 }} />
          <Box>
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 20, lineHeight: 1.2 }}>
              Invoice Dashboard
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: 13 }}>
              Download and manage invoices
            </Typography>
          </Box>
        </Box>

        {/* Download CSV strip */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <TextField
            label="From Date"
            type="date"
            size="small"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{
              background: "#fff",
              borderRadius: "8px",
              "& .MuiOutlinedInput-root": { borderRadius: "8px" },
            }}
          />
          <TextField
            label="To Date"
            type="date"
            size="small"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{
              background: "#fff",
              borderRadius: "8px",
              "& .MuiOutlinedInput-root": { borderRadius: "8px" },
            }}
          />
          <Button
            variant="contained"
            onClick={handleDownload}
            disabled={loading}
            startIcon={loading ? null : <DownloadIcon />}
            sx={{
              background: "linear-gradient(135deg, #016551, #014d3d)",
              color: "#fff",
              fontWeight: 600,
              fontSize: 13,
              px: 2.5,
              py: 1,
              borderRadius: "8px",
              textTransform: "none",
              boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
              "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)" },
              "&.Mui-disabled": { background: "#e2e8f0", color: "#9ca3af", boxShadow: "none" },
            }}
          >
            {loading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Download CSV"}
          </Button>
        </Box>
      </Box>

      {/* Invoice Table */}
      <InvoiceTable />
    </Box>
  );
};

export default InvoiceDashboard;
